import { combineReducers } from 'redux';
import {
    SET_SELECTED_BUSINESS,
    SET_SELECTED_LOCATION,
    SET_SELECTED_LOCATIONS,
    SET_SELECTED_DASHBOARD_TILES,
    SET_KIOSK_PROFILE,
    SET_STORE_PROFILE
} from '../actions/actionTypes';
import {
    SET_PRINTER_PROFILES,
    UPSERT_PRINTER_PROFILE,
    DELETE_PRINTER_PROFILE,
    SET_MODULE_PRINTER_PROFILE,
    SET_MODULE_TEMPLATE,
    SET_LABELER_MODULE_SETTINGS,
} from '../../modules/labeler/data/actions/actionTypes';
import authReducer from '../../modules/auth/data/reducers/authReducer';
import { LocationBusiness } from '../../modules/shared/models';
import { KioskProfileSettings } from '../../modules/kiosk/models/models';
import kioskauthReducer from '../../modules/kiosk/data/reducers/kioskauthReducer';
import { StoreProfileSettings } from '../../modules/shopping-cart/models/models';
import {
    clearModuleSelectionsForProfile,
    removePrinterProfileFromList,
    upsertPrinterProfileInList,
} from '../../modules/labeler/lib/printerProfiles';

export interface UserSettings {
    userData: any;
    labelerModule: LabelerModuleSettings;
    userLocation: any;
    selectedBusiness: LocationBusiness;
    selectedLocations: Location[];
    selectedDashboardTiles?: string[];
}

export interface AutoAdjustLabelSettings {
    currentPrintWidth: string;
    isAutoAdjustEnabled: boolean;
}

export interface MultiPrintSettings {
    isMultiPrintEnabled: boolean;
    isResetPrintQuantity: boolean;
}

export interface NetworkPrinterSettings {
    ip: string;
    port: string;
}

/** Print entry points that remember a selected printer profile. */
export type PrintModuleId = 'labeler' | 'tracker' | 'priceUpdate' | 'productDetail';

export interface PrinterProfile {
    id: string;
    name: string;
    networkPrinterSettings: NetworkPrinterSettings;
    autoAdjustLabel: AutoAdjustLabelSettings;
    multiPrintSettings: MultiPrintSettings;
    reportEmail: string | null;
}

export interface LabelerModuleSettings {
    printerProfiles: PrinterProfile[];
    /** Last-selected profile id per print module. Missing/cleared = user must re-pick. */
    modulePrinterProfileIds: Partial<Record<PrintModuleId, string>>;
    /** Last-selected label template id per print module (independent of printer profile). */
    moduleTemplateIds: Partial<Record<PrintModuleId, number>>;
}

const initialPrinterProfiles: PrinterProfile[] = [];
const initialModulePrinterProfileIds: Partial<Record<PrintModuleId, string>> = {};
const initialModuleTemplateIds: Partial<Record<PrintModuleId, number>> = {};

const printerProfilesReducer = (
    state: PrinterProfile[] = initialPrinterProfiles,
    action: {
        type: string;
        profiles?: PrinterProfile[];
        profile?: PrinterProfile;
        profileId?: string;
        labelerModule?: LabelerModuleSettings;
    },
) => {
    switch (action.type) {
        case SET_LABELER_MODULE_SETTINGS:
            return action.labelerModule?.printerProfiles
                ? [...action.labelerModule.printerProfiles]
                : state;
        case SET_PRINTER_PROFILES:
            return action.profiles ? [...action.profiles] : state;
        case UPSERT_PRINTER_PROFILE:
            return action.profile
                ? upsertPrinterProfileInList(state, action.profile)
                : state;
        case DELETE_PRINTER_PROFILE:
            return action.profileId
                ? removePrinterProfileFromList(state, action.profileId)
                : state;
        default:
            return state;
    }
};

const modulePrinterProfileIdsReducer = (
    state: Partial<Record<PrintModuleId, string>> = initialModulePrinterProfileIds,
    action: {
        type: string;
        moduleId?: PrintModuleId;
        profileId?: string | null;
        labelerModule?: LabelerModuleSettings;
        modulePrinterProfileIds?: Partial<Record<PrintModuleId, string>>;
    },
) => {
    switch (action.type) {
        case SET_LABELER_MODULE_SETTINGS:
            return action.labelerModule?.modulePrinterProfileIds
                ? { ...action.labelerModule.modulePrinterProfileIds }
                : state;
        case SET_MODULE_PRINTER_PROFILE: {
            if (!action.moduleId) {
                return state;
            }
            const next = { ...state };
            if (!action.profileId) {
                delete next[action.moduleId];
            } else {
                next[action.moduleId] = action.profileId;
            }
            return next;
        }
        case DELETE_PRINTER_PROFILE:
            return action.profileId
                ? clearModuleSelectionsForProfile(state, action.profileId)
                : state;
        default:
            return state;
    }
};

const moduleTemplateIdsReducer = (
    state: Partial<Record<PrintModuleId, number>> = initialModuleTemplateIds,
    action: {
        type: string;
        moduleId?: PrintModuleId;
        templateId?: number | null;
        labelerModule?: LabelerModuleSettings;
    },
) => {
    switch (action.type) {
        case SET_LABELER_MODULE_SETTINGS:
            return action.labelerModule?.moduleTemplateIds
                ? { ...action.labelerModule.moduleTemplateIds }
                : state;
        case SET_MODULE_TEMPLATE: {
            if (!action.moduleId) {
                return state;
            }
            const next = { ...state };
            if (!action.templateId) {
                delete next[action.moduleId];
            } else {
                next[action.moduleId] = action.templateId;
            }
            return next;
        }
        default:
            return state;
    }
};

const labelerSettingsReducer = combineReducers({
    printerProfiles: printerProfilesReducer,
    modulePrinterProfileIds: modulePrinterProfileIdsReducer,
    moduleTemplateIds: moduleTemplateIdsReducer,
});

const selectedLocationReducer = (state = null as any, action: any) => {
    switch (action.type) {
        case SET_SELECTED_LOCATION:
            if (action.location) {
                return {
                    ...action.location
                };
            } else {
                return state;
            }
        default: return state;
    }
};
const selectedLocationsReducer = (state = [] as any, action: any) => {
    switch (action.type) {
        case SET_SELECTED_LOCATIONS:
            if (action.locations) {
                return [
                    ...action.locations
                ];
            } else {
                return state;
            }
        default: return state;
    }
};
const selectedBusinessReducer = (state = null as unknown as LocationBusiness, action: any) => {
    switch (action.type) {
        case SET_SELECTED_BUSINESS:
            if (action.business) {
                return {
                    ...action.business
                };
            } else {
                return state;
            }
        default: return state;
    }
};
const selectedDashboardTilesReducer = (state = [] as string[], action: any) => {
    switch (action.type) {
        case SET_SELECTED_DASHBOARD_TILES:
            if (action.selectedOptions) {
                return [...action.selectedOptions];
            } else {
                return state;
            }
        default: return state;
    }
};

const selectedKioskProfileReducer = (state = null as KioskProfileSettings, action: any) => {
    switch (action.type) {
        case SET_KIOSK_PROFILE:
            if (action.data) {
                return { ...action.data };
            } else {
                return state;
            }
        default: return state;
    }
};

const selectedStoreProfileReducer = (state = null as StoreProfileSettings, action: any) => {
    switch (action.type) {
        case SET_STORE_PROFILE:
            if (action.data) {
                return { ...action.data };
            } else {
                return state;
            }
        default: return state;
    }
};

export const userDataReducer = combineReducers({
    userData: authReducer,
    labelerModule: labelerSettingsReducer,
    userLocation: selectedLocationReducer,
    selectedBusiness: selectedBusinessReducer,
    selectedDashboardTiles: selectedDashboardTilesReducer,
    selectedLocations: selectedLocationsReducer,
    selectedKioskProfile: selectedKioskProfileReducer,
    selectedStoreProfile: selectedStoreProfileReducer,
});
